/**
 * The breakdown export controls: Print PDF · Copy Image · Copy Text ▾.
 *
 * One component for both shells — the InsuranceMasterModal header (fed through
 * onPrintControls) and the standalone breakdown modal's in-pane group — so the
 * two never drift. `controls` is the payload InsuranceBreakdownMasterModal
 * publishes: { printPDF, copyImage, copyText(kind), canPrint, isPrinting,
 * isCopyingImage, isCopyingText, mode, formatChoice }.
 *
 * mode "custom": the office has its own breakdown sheet (OfficeBreakdownFormat)
 * and every export is in it — Copy Text is then one button, their format only.
 * formatChoice (super admins only): {value, options, onChange} — preview a
 * draft, or the standard exports, on this real breakdown.
 */
import React from "react";
import { Button, Dropdown, Select } from "antd";
import { CopyOutlined, DownOutlined, FileImageOutlined, PrinterOutlined } from "@ant-design/icons";

const TEXT_ITEMS = [
  { key: "detailed", label: "Detailed (plan info)" },
  { key: "short", label: "Short form" },
];

export default function BreakdownExportButtons({ controls }) {
  if (!controls) return null;
  const disabled = !controls.canPrint;
  const custom = controls.mode === "custom";
  const choice = controls.formatChoice;
  const inFormat = custom ? " — in this office's own breakdown format" : "";
  return (
    <>
      {choice && choice.options && choice.options.length > 1 ? (
        <Select
          size="small"
          value={choice.value}
          options={choice.options}
          onChange={choice.onChange}
          popupMatchSelectWidth={false}
          style={{ minWidth: 150 }}
          title="Which format the exports use (super admins only)"
          data-testid="breakdown-format-choice"
        />
      ) : null}
      <Button
        icon={<PrinterOutlined />}
        onClick={() => controls.printPDF && controls.printPDF()}
        loading={!!controls.isPrinting}
        disabled={disabled}
        title={`Print this breakdown${inFormat}`}
        data-onboarding-id="breakdown-print-pdf"
      >
        Print PDF
      </Button>
      <Button
        icon={<FileImageOutlined />}
        onClick={() => controls.copyImage && controls.copyImage()}
        loading={!!controls.isCopyingImage}
        disabled={disabled}
        title={`Copy a picture of this breakdown to the clipboard and save it to Downloads${inFormat}`}
        data-onboarding-id="breakdown-copy-image"
      >
        Copy Image
      </Button>
      {custom ? (
        <Button
          icon={<CopyOutlined />}
          onClick={() => controls.copyText && controls.copyText()}
          loading={!!controls.isCopyingText}
          disabled={disabled}
          title={`Copy this breakdown as text${inFormat}`}
          data-onboarding-id="breakdown-copy-text"
        >
          Copy Text
        </Button>
      ) : (
        <Dropdown
          disabled={disabled}
          trigger={["click"]}
          menu={{
            items: TEXT_ITEMS,
            onClick: ({ key }) => controls.copyText && controls.copyText(key),
          }}
        >
          <Button
            icon={<CopyOutlined />}
            loading={!!controls.isCopyingText}
            disabled={disabled}
            title="Copy this breakdown as text"
            data-onboarding-id="breakdown-copy-text"
          >
            Copy Text <DownOutlined style={{ fontSize: 10 }} />
          </Button>
        </Dropdown>
      )}
    </>
  );
}

/** The super admin's choice of format: the live one, a draft, or the standard exports. */
export function formatChoiceOptions(versions) {
  const list = versions || [];
  const live = list.find((v) => v.isActive);
  const options = [{ value: "auto", label: live ? `Format: office v${live.version}` : "Format: standard" }];
  list.filter((v) => !v.isActive).forEach((v) => options.push({ value: String(v.id), label: `Format: draft v${v.version}` }));
  if (live) options.push({ value: "standard", label: "Format: standard" });
  return options;
}
